import type { CSSProperties, ReactElement } from "react";

import { useState } from "react";

import { Avatar, Chip, ContextMenu, Icon } from "../design-system";
import { MENU_ROW, MENU_SURFACE } from "./menuSurface";
import { useSkin, type Skin } from "./skin";
import type { SessionView } from "../session/useSession";

/**
 * Der **Benutzerfuß** der Sidebar — der vierte Block des Rahmens.
 *
 * Avatar rund, Name (`--fs-13`/600) über der Kennung (`--fs-11`/`--fg-7`), dazu
 * `chevrons-up-down`; oben abgetrennt durch eine Haarlinie in `--line-f`
 * (`FRAME.md`, „Sidebar"). Er ist der Knopf zum **Benutzermenü**, und das
 * öffnet **nach oben** — es hängt am Fuß, und unter dem Fuß ist nichts.
 *
 * **Die zweite Zeile trägt die ERP-Kennung und keine E-Mail.** Der Rahmen zeigt
 * dort eine Adresse; dieses Werkzeug kennt keine. Die Identität kommt aus dem
 * SilverERP (`AGENTS.md`: „das ERP liefert die Identität, nicht die Rolle"), und
 * was es liefert, ist die Anmeldekennung — `session.state.displayName`. Sie steht
 * hier, weil die Zeile einen Namen zweiter Ordnung braucht und eine erfundene
 * Adresse eine Behauptung wäre. Ist sie dasselbe Wort wie der Anzeigename, bleibt
 * die Zeile weg statt denselben Namen zweimal zu nennen.
 *
 * **Abmelden und die Rolle stehen seit #135 hier** und nicht mehr rechts in der
 * Kopfleiste: die Rolle als `Chip` in der Kopfzeile des Menüs, `session.signOut`
 * am letzten Eintrag. Der Verbindungszustand bleibt in der Kopfleiste, wo #40 ihn
 * hingestellt hat — er ist eine Auskunft über die Gegenstellen und keine über die
 * angemeldete Person.
 *
 * **Die Fassungszeile hängt seit #158 hier** und nicht mehr an der Logo-Zeile: Wo
 * ein Produkt keine Mandanten hat, trägt die Logo-Zeile Signet und Produktnamen
 * ohne Menü und ohne Auslöser — ein Knopf an der Stelle des Unit-Menüs, der etwas
 * anderes öffnet, machte dieselbe Geste in zwei Produkten zu zwei verschiedenen
 * Dingen. Die laufende Fassung steht deshalb in der Feinschrift des Benutzermenüs
 * und ist von dort der **einzige** Weg in die Neuerungen (ADR-0029).
 *
 * **Drei Einträge der Vorlage fallen weg** — Verlauf, Favoriten und Station
 * sperren —, weil hier nichts dahinter liegt. „Einstellungen ▸" kam mit #159
 * dazu, mit genau einem Inhalt: dem biometrischen Zugang unter „Anmeldedaten
 * ändern". Ausgeschrieben steht beides in `design/VENDOR.md`; `AppShell.jsx`
 * zeigt an dieser Stelle ein verkürztes Menü, und das ist eine Vereinfachung des
 * Kits und keine Spezifikation (`FRAME.md`, „Abweichung des Kits").
 */
export function UserFooter({
  session,
  collapsed,
  version,
  onOpenVersionHistory,
  onOpenCredentials,
}: {
  session: SessionView;
  collapsed: boolean;
  /** Die laufende Fassung, oder `null` — dann fehlt die Fassungszeile ganz. */
  version: string | null;
  onOpenVersionHistory: () => void;
  /** „Einstellungen ▸ Anmeldedaten ändern" — der Ort des biometrischen Zugangs. */
  onOpenCredentials: () => void;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const name = session.person?.displayName ?? session.state?.displayName ?? "";
  const identity = session.state?.displayName ?? "";
  // Dieselbe Kennung zweimal zu nennen wäre keine zweite Zeile, sondern ein Echo.
  const second = identity === name ? "" : identity;

  return (
    // Über dem Inhalt daneben: Das Menü klappt nach oben und über ihn hinweg.
    <div style={{ position: "relative", flex: "none", zIndex: 45 }}>
      {open ? (
        <UserMenu
          name={name}
          identity={second}
          role={session.person?.role ?? null}
          version={version}
          onClose={() => setOpen(false)}
          onOpenVersionHistory={() => {
            setOpen(false);
            onOpenVersionHistory();
          }}
          onOpenCredentials={() => {
            setOpen(false);
            onOpenCredentials();
          }}
          onSignOut={session.signOut}
        />
      ) : null}

      <button
        type="button"
        className="erp-nav-item"
        aria-expanded={open}
        // Eingeklappt steht nur der Avatar da; ohne den Namen am Knopf wäre der
        // Fuß dann unbedienbar.
        title={name}
        onClick={() => setOpen(!open)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          width: "100%",
          // Die Maße der Vorlage, unverändert: `padding: "12px 16px"`
          // (`reference/AppShell.jsx`, `Sidebar`). Für 12 und 16 führt das System
          // an dieser Stelle kein Token, und der Rahmen schreibt sie selbst als
          // Zahl.
          paddingTop: 12,
          paddingBottom: 12,
          paddingLeft: 16,
          paddingRight: 16,
          border: "none",
          borderTop: "1px solid var(--line-f)",
          background: "var(--nav-item-hot-bg, transparent)",
          cursor: "pointer",
          overflow: "hidden",
          textAlign: "left",
          fontFamily: "var(--font-sans)",
        }}
      >
        <Avatar />
        {collapsed ? null : (
          <span style={{ flex: 1, minWidth: 0, lineHeight: "var(--lh-snug)" }}>
            <span
              style={{
                display: "block",
                fontSize: "var(--fs-13)",
                fontWeight: "var(--fw-semibold)",
                color: "var(--fg-1)",
              }}
            >
              {name}
            </span>
            {second === "" ? null : (
              <span
                style={{
                  display: "block",
                  fontSize: "var(--fs-11)",
                  color: "var(--fg-7)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {second}
              </span>
            )}
          </span>
        )}
        {collapsed ? null : <Icon name="chevrons-up-down" size={18} color="var(--fg-7)" />}
      </button>
    </div>
  );
}

/**
 * Das **Benutzermenü**: Kopfzeile mit Name, Kennung und Rolle, darunter die
 * Gruppen — Einstellungen ▸ und Interface ▸, dann Abmelden —, getrennt durch eine
 * Haarlinie, und ganz unten die Fassungszeile.
 *
 * Es öffnet **nach oben**: `bottom` und nicht `top`, gemessen an der Höhe des
 * Fußes (Avatar 34 plus zweimal 12 Polster). Nach links tritt es aus der Spalte
 * nicht heraus, nach rechts darf es das — eingeklappt ist die Spalte 64px breit,
 * und ein Menü, das sich darin einrichtete, wäre keins.
 */
function UserMenu({
  name,
  identity,
  role,
  version,
  onClose,
  onOpenVersionHistory,
  onOpenCredentials,
  onSignOut,
}: {
  name: string;
  identity: string;
  role: string | null;
  version: string | null;
  onClose: () => void;
  onOpenVersionHistory: () => void;
  onOpenCredentials: () => void;
  onSignOut: () => void;
}): ReactElement {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [interfaceOpen, setInterfaceOpen] = useState(false);

  return (
    <>
      {/* Der Klick daneben schließt. Er liegt unter dem Menü und über allem
          sonst — sonst schlösse ein Klick im Menü es sofort wieder. */}
      <div
        aria-hidden="true"
        onClick={onClose}
        style={{ position: "fixed", inset: 0, zIndex: 40 }}
      />
      <div
        role="menu"
        aria-label="Benutzermenü"
        style={{
          ...MENU_SURFACE,
          position: "absolute",
          left: 12,
          bottom: 58,
          zIndex: 41,
          minWidth: 236,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            paddingTop: 7,
            paddingBottom: 8,
            paddingLeft: 10,
            paddingRight: 10,
            borderBottom: "1px solid var(--line-e)",
            marginBottom: 4,
          }}
        >
          <span
            style={{
              fontSize: "var(--fs-12-5)",
              fontWeight: "var(--fw-semibold)",
              color: "var(--fg-1)",
            }}
          >
            {name}
          </span>
          {identity === "" ? null : (
            <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-7)" }}>{identity}</span>
          )}
          {/* Die Rolle stand bis #135 neben „Angemeldet als" in der Kopfleiste.
              Sie ist ein Etikett und kein Zustand, also ein `Chip` — `Badge`, den
              das Ticket nennt, hat #141 in beides zerlegt. */}
          {role === null ? null : (
            <span style={{ marginTop: 4 }}>
              <Chip>{role}</Chip>
            </span>
          )}
        </div>

        {/* Jede Zeile mit Untermenü steht in ihrem **eigenen** Kontextmenü, und
            das Untermenü darin. So öffnet es seitlich an *seiner* Zeile, ohne
            dass irgendwo eine Zeilenhöhe ausgerechnet wird — `top` gleicht nur
            noch den Innenabstand aus. Eine gemessene Höhe wäre ein Wert, der beim
            nächsten Eintrag still falsch wird. */}
        <div style={{ position: "relative" }}>
          <ContextMenu
            style={FLAT_MENU}
            items={[
              {
                icon: "settings",
                label: "Einstellungen",
                submenu: true,
                onClick: () => setSettingsOpen(!settingsOpen),
              },
            ]}
          />
          {settingsOpen ? <SettingsMenu onOpenCredentials={onOpenCredentials} /> : null}
        </div>

        <div style={{ position: "relative" }}>
          <ContextMenu
            style={FLAT_MENU}
            items={[
              {
                icon: "sun",
                label: "Interface",
                submenu: true,
                onClick: () => setInterfaceOpen(!interfaceOpen),
              },
            ]}
          />
          {interfaceOpen ? <InterfaceMenu /> : null}
        </div>

        <ContextMenu
          style={FLAT_MENU}
          items={[
            { divider: true },
            { icon: "log-out", label: "Abmelden", danger: true, onClick: onSignOut },
          ]}
        />

        {version === null ? null : (
          <VersionRow version={version} onOpen={onOpenVersionHistory} />
        )}
      </div>
    </>
  );
}

/**
 * Die **Fassungszeile** am Fuß des Benutzermenüs: die laufende Fassung in der
 * Feinschrift des Rahmens (`--fs-11`/`--fg-9`, die Maße der Augenbraue), und der
 * einzige Weg in die **Neuerungen**.
 *
 * Sie steht unter den Einträgen und durch eine Haarlinie von ihnen getrennt: Sie
 * ist keine der drei Gruppen, die `FRAME.md` unter „Benutzermenü" führt, sondern
 * die Auskunft, was hier läuft — und ein Klick darauf führt dorthin, wo steht,
 * was sich damit geändert hat.
 *
 * **Ohne bekannte Fassung fehlt sie**, statt leer dazustehen: Ein Knopf, hinter
 * dem nichts steht, ist ein Versprechen. Diese Regel stand bis #158 an der
 * Logo-Zeile und ist mit der Zeile umgezogen.
 */
function VersionRow({
  version,
  onOpen,
}: {
  version: string;
  onOpen: () => void;
}): ReactElement {
  return (
    <div style={{ borderTop: "1px solid var(--line-e)", marginTop: 4, paddingTop: 4 }}>
      <button
        type="button"
        className="erp-menu-item"
        onClick={onOpen}
        style={{
          ...MENU_ROW,
          fontSize: "var(--fs-11)",
          color: "var(--fg-9)",
        }}
      >
        <Icon name="history" size={14} />
        <span style={{ flex: 1 }}>Neuerungen</span>
        <span style={{ fontVariantNumeric: "tabular-nums" }}>{version}</span>
      </button>
    </div>
  );
}

/**
 * Das Kontextmenü **ohne eigene Fläche**: Die trägt schon das Benutzermenü, in
 * dem es steht. Ohne das Ablegen säßen zwei Rahmen ineinander.
 */
const FLAT_MENU: CSSProperties = {
  width: "100%",
  border: "none",
  background: "transparent",
  boxShadow: "none",
  padding: 0,
};

/**
 * Das Untermenü **Einstellungen** — mit genau einem Eintrag.
 *
 * `FRAME.md` führt unter „Benutzermenü" sechs (Benutzereinstellungen,
 * Anmeldedaten ändern, Benachrichtigungen, Dialogeinstellungen,
 * Tastaturbelegung, Geräteeinstellungen). Fünf davon hat dieses Werkzeug nicht,
 * und ein Eintrag, hinter dem nichts liegt, ist ein Versprechen — dieselbe
 * Regel, die die Fassungszeile ohne bekannte Fassung wegnimmt. Ausgeschrieben
 * steht die Auslassung in `design/VENDOR.md`.
 *
 * **„Anmeldedaten ändern" ist der Ort des biometrischen Zugangs** und nicht
 * „Geräteeinstellungen": Er ist eine Anmeldeart, und dort sucht ihn niemand.
 */
function SettingsMenu({ onOpenCredentials }: { onOpenCredentials: () => void }): ReactElement {
  return (
    <div
      role="menu"
      aria-label="Einstellungen"
      style={{
        ...MENU_SURFACE,
        position: "absolute",
        left: "100%",
        top: 4,
        marginLeft: 4,
        zIndex: 42,
        minWidth: 212,
      }}
    >
      <button type="button" className="erp-menu-item" onClick={onOpenCredentials} style={MENU_ROW}>
        <Icon name="id-card" size={15} color="var(--fg-9)" />
        <span style={{ flex: 1, color: "var(--fg-3)" }}>Anmeldedaten ändern</span>
      </button>
    </div>
  );
}

/** Die beiden Skins, in der Reihenfolge des Rahmens. */
const SKINS: readonly { id: Skin; label: string }[] = [
  { id: "light", label: "Hell" },
  { id: "dark", label: "Dunkel" },
];

/**
 * Das Untermenü **Interface**: Hell und Dunkel, die geltende mit `check` in
 * `--ac-blue`. Mindestens 212px breit, seitlich geöffnet (`FRAME.md`,
 * „Benutzermenü").
 */
function InterfaceMenu(): ReactElement {
  const { skin, choose } = useSkin();

  return (
    <div
      role="menu"
      aria-label="Interface"
      style={{
        ...MENU_SURFACE,
        position: "absolute",
        left: "100%",
        top: 4,
        marginLeft: 4,
        zIndex: 42,
        minWidth: 212,
      }}
    >
      {SKINS.map((one) => (
        <button
          key={one.id}
          type="button"
          className="erp-menu-item"
          onClick={() => choose(one.id)}
          style={{
            ...MENU_ROW,
            color: one.id === skin ? "var(--fg-1)" : "var(--fg-3)",
            fontWeight: one.id === skin ? "var(--fw-semibold)" : "var(--fw-regular)",
          }}
        >
          <span style={{ flex: 1 }}>{one.label}</span>
          {one.id === skin ? <Icon name="check" size={15} color="var(--ac-blue)" /> : null}
        </button>
      ))}
    </div>
  );
}
